import { Message } from 'element-ui'
import store from '../store'
import { getToken, isTokenExpired } from './token'
import { redirectToLogin } from './navigate'
import { PERM } from './permission'

const AUTH_HINTS = ['登录', '未登录', '过期', '失效', 'token', 'Token', 'JWT', '认证', '鉴权']

/** 项目监督员被后端拦截的写入请求（非登录失效） */
export function isSupervisorReadOnly403(data) {
  const msg = extractResponseMessage(data)
  return !!(msg && msg.includes('项目监督员'))
}

/** 接口路径 -> 所需权限（用于识别「前端有权限、后端 403」的登录态不同步） */
const API_PERMISSION_HINTS = [
  { match: '/files/', code: PERM.FILES_VIEW },
  { match: '/training/', code: PERM.TRAINING_TASKS },
  { match: '/sign/', code: PERM.SIGN_TASKS },
  { match: '/exam/', code: PERM.EXAM_TASKS },
  { match: '/entry/', code: PERM.ENTRY_PROGRAMS }
]

export function isAuthFailureMessage(msg) {
  if (!msg || typeof msg !== 'string') return false
  return AUTH_HINTS.some(h => msg.includes(h))
}

export function currentToken() {
  return store.state.token || getToken()
}

export function tokenLooksExpired(token) {
  return !token || isTokenExpired(token)
}

function localUserHasPermission(code) {
  const u = store.state.user
  if (!u) return false
  if (u.roleCode === 'SUPER_ADMIN') return true
  return (u.permissions || []).includes(code)
}

/** 本地登录信息里已有该接口权限，但后端仍 403 → 多为 JWT/部署后未重新登录 */
export function isPermissionMismatch403(cfg, data) {
  const user = store.state.user
  if (user && user.roleCode === 'PROJECT_SUPERVISOR') return false
  if (isSupervisorReadOnly403(data)) return false
  const url = cfg && cfg.url ? String(cfg.url) : ''
  if (!url) return false
  for (const hint of API_PERMISSION_HINTS) {
    if (!url.includes(hint.match)) continue
    if (localUserHasPermission(hint.code)) return true
  }
  const msg = data && data.message
  return !!(msg && msg.includes('无此操作权限') && store.state.user && store.state.user.roleCode === 'PROJECT_ADMIN')
}

/** 登录页 / 认证接口无需 token */
function isPublicAuthContext(cfg) {
  if (cfg && cfg.skipAuth) return true
  if (cfg && cfg.url && String(cfg.url).startsWith('/auth/')) return true
  return typeof window !== 'undefined' && window.location.pathname === '/login'
}

/** 登录失效：清缓存并跳转登录页（防并发重复弹窗） */
export function handleSessionExpired(message) {
  const onLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login'
  store.commit('logout')
  if (onLoginPage) return
  redirectToLogin()
  Message.warning(message || '登录已过期，请重新登录')
}

export function rejectSessionExpired(message) {
  handleSessionExpired(message)
  return Promise.reject(new Error('SESSION_EXPIRED'))
}

/** 请求发出前：token 已过期则不再调用接口（登录页与 /auth/* 除外） */
export function guardRequestAuth(config) {
  if (isPublicAuthContext(config)) return null
  const token = currentToken()
  if (tokenLooksExpired(token)) {
    return rejectSessionExpired('登录已过期，请重新登录')
  }
  return null
}

function rejectApiPayload(data, fallbackMessage) {
  const msg = extractResponseMessage(data) || fallbackMessage
  const err = new Error(msg)
  if (data && typeof data === 'object' && data.code != null) err.code = data.code
  err.data = data && typeof data === 'object' ? data : { message: msg }
  return Promise.reject(err)
}

/** 403/401 响应体可能是纯文本（如 Spring CORS: Invalid CORS request） */
export function extractResponseMessage(data) {
  if (!data) return ''
  if (typeof data === 'string') {
    const t = data.trim()
    if (!t) return ''
    if (t.includes('Invalid CORS')) {
      return '跨域校验失败，请使用 https://www.gxjngs.cn/login 访问'
    }
    return t.length > 120 ? t.slice(0, 120) : t
  }
  if (typeof data === 'object' && data.message) return data.message
  return ''
}

/** 401 / 疑似登录失效的 403 */
export function handleAuthHttpError(status, data, cfg) {
  const onLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login'
  const isAuthRequest = isPublicAuthContext(cfg)
  const msg = data && data.message

  if (status === 401) {
    if (onLoginPage || isAuthRequest) {
      if (!cfg.silent) {
        Message.error(msg || '登录失败，请检查账号、密码和验证码')
      }
      return rejectApiPayload(data, msg || '登录失败，请检查账号、密码和验证码')
    }
    return rejectSessionExpired(msg || '登录已过期，请重新登录')
  }

  if (status === 403) {
    if (onLoginPage || isAuthRequest) {
      if (!cfg.silent) {
        Message.error(msg || '请求失败')
      }
      return rejectApiPayload(data, msg || '请求失败')
    }
    if (isSupervisorReadOnly403(data)) {
      if (!cfg.silent) {
        Message.warning(msg || '项目监督员仅可查看与下载，无权修改数据')
      }
      const err = new Error(msg || '项目监督员仅可查看与下载，无权修改数据')
      err.code = 403
      err.data = data && typeof data === 'object' ? data : { message: msg }
      return Promise.reject(err)
    }
    const token = currentToken()
    if (tokenLooksExpired(token) || !token || isAuthFailureMessage(msg) || isPermissionMismatch403(cfg, data)) {
      return rejectSessionExpired(
        isPermissionMismatch403(cfg, data) && token && !tokenLooksExpired(token)
          ? '登录状态已失效（常见于部署后），请重新登录'
          : (msg || '登录已过期，请重新登录')
      )
    }
    if (!cfg.silent) {
      Message.error(msg || '当前账号无此操作权限，请联系超级管理员')
    }
    return Promise.reject(data || new Error('FORBIDDEN'))
  }

  return null
}
